import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Flex, Icon, Text } from '@kognitos/lattice';

import { useAutomationGetActivePlan } from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import { useRunGetActivePlan } from '@/generated/hooks/rain/v1/run_scoped/thread_service';
import type { V1Plan } from '@/generated/hooks/rain/models';
import { useScrollEdges } from '@/shared/hooks/use-scroll-edges';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import {
  RAIN_FULL_WIDTH_CARD,
  type TRainFullWidthCard,
} from '../full-width-card';
import { isPlanResolved } from '../../utils/plan-stream';

import { getPlanProgress, RainPlanView } from './RainPlanView';

/** `stream` is a finished plan in the transcript; the others pin the live one. */
export type TRainTodoVariant = 'floating' | 'inline' | 'stream';

/**
 * What every scope's Todo card needs. The ids that locate the plan differ by
 * scope, so each wrapper adds its own rather than the shared shape assuming an
 * automation.
 */
export interface IRainTodoCardBaseProps {
  agentId: string;
  threadId: string;
  variant: TRainTodoVariant;
  /** When true, the parent stream is actively running; enable backstop polling. */
  isSending?: boolean;
  /** `chat.isLivePlanRetired` — the transcript has it, so this stands down. */
  isRetired?: boolean;
  className?: string;
}

export interface IRainTodoCardProps extends IRainTodoCardBaseProps {
  /** Automation the thread belongs to. Scopes the active-plan query. */
  automationId: string;
}

export interface IRainRunTodoCardProps extends IRainTodoCardProps {
  /** Run the thread belongs to. Switches the query to the run-scoped endpoint. */
  runId: string;
}

export interface IRainStreamPlanCardProps {
  /** Rebuilt from the thread's own plan events, not fetched. */
  plan: V1Plan;
  className?: string;
}

// Backstop only. The primary refresh path is the stream-event-driven
// invalidation in useRainThreadMessages on `plan_mark_step` tool_result.
// This catches edge cases (missed event, disconnect/reconnect).
const POLL_INTERVAL_MS = 10_000;

interface IRainTodoCardViewProps {
  activePlan: V1Plan | undefined;
  isError: boolean;
  variant: TRainTodoVariant;
  isRetired?: boolean;
  /** A transcript record: start collapsed, never auto-expand, never retire. */
  isHistorical?: boolean;
  className?: string;
}

/**
 * Presentational Todo card. Scope-agnostic — the active plan is resolved by a
 * scope-specific wrapper (`RainTodoCard` / `RainRunTodoCard`) and passed in.
 */
function RainTodoCardView({
  activePlan,
  isError,
  variant,
  isRetired = false,
  isHistorical = false,
  className,
}: IRainTodoCardViewProps) {
  const { t } = useTranslation('rain');

  const { total, completed, failed } = useMemo(
    () => getPlanProgress(activePlan),
    [activePlan],
  );

  // Resolution, not progress: a failed or skipped step still counts as done
  const isFinished = useMemo(() => isPlanResolved(activePlan), [activePlan]);

  // A transcript card only exists once settled, so unresolved means abandoned.
  const isAbandoned = isHistorical && !isFinished && total > 0;

  const hasActivePlan =
    Boolean(activePlan) && !isError && (isHistorical || !isRetired);

  const [isExpanded, setIsExpanded] = useState(
    !isHistorical && hasActivePlan && total > 0 && !isFinished,
  );

  // Re-sync expand/collapse default when the plan transitions into/out of
  // existence or into the "finished" state. User-driven toggles after that
  // moment win — we only nudge on these specific transitions.
  // eslint-disable-next-line no-restricted-syntax -- reason: Derives UI default from server state transitions; user-driven toggles are not derivable from the query
  useEffect(() => {
    if (isHistorical) return;
    if (!hasActivePlan) {
      setIsExpanded(false);
      return;
    }
    if (isFinished) {
      setIsExpanded(false);
      return;
    }
    if (total > 0) {
      setIsExpanded(true);
    }
  }, [isHistorical, hasActivePlan, isFinished, total]);

  // Only render when an active plan exists — both floating and inline. The
  // empty "Used for longer tasks ›" header from the Figma is intentionally
  // omitted; the card appears as soon as the agent activates a plan.
  // Early return is AFTER all hooks to comply with the Rules of Hooks.
  if (!hasActivePlan) {
    return null;
  }

  const containerClassName = cn(
    'overflow-hidden',
    variant === 'floating' &&
      'absolute top-5 right-5 z-10 w-[250px] rounded-lg border border-border bg-card shadow-sm',
    // Fill instead of a border: it reads as the top of the composer it sits on
    variant === 'inline' && 'w-full rounded-t-xl bg-muted',
    variant === 'stream' &&
      'w-full rounded-xl mb-6 border border-border bg-card shadow-sm',
    className,
  );

  const title = (() => {
    if (isAbandoned)
      return t('rain.todo.titleStopped', { completed, count: total });
    if (!isFinished) return t('rain.todo.title');
    return failed > 0
      ? t('rain.todo.titleSummaryFailed', { count: total, failed })
      : t('rain.todo.titleSummary', { count: total });
  })();

  const headerLabel = (
    <Text as="span" level="xSmall" color="muted" className="tabular-nums">
      {t('rain.todo.count', { completed, total })}
    </Text>
  );

  const chevron: 'ChevronDown' | 'ChevronUp' = isExpanded
    ? 'ChevronUp'
    : 'ChevronDown';

  const canToggle = total > 0;

  return (
    <div
      className={containerClassName}
      data-testid="rain-todo-card"
      data-variant={variant}
      data-state={isFinished ? 'completed' : 'active'}
    >
      <button
        type="button"
        onClick={() => {
          if (canToggle) setIsExpanded(prev => !prev);
        }}
        className={cn(
          'flex w-full items-center gap-2 px-5 py-3 text-left',
          canToggle ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default',
        )}
        disabled={!canToggle}
        aria-expanded={canToggle ? isExpanded : undefined}
      >
        <Text as="span" level="small" weight="medium">
          {title}
        </Text>
        <Flex
          align="center"
          gap={1}
          className="ml-auto min-w-0 flex-shrink-0 text-muted-foreground"
        >
          {headerLabel}
          <Icon type={chevron} size="sm" className="shrink-0" />
        </Flex>
      </button>

      {canToggle && isExpanded && activePlan && (
        <TodoStepList
          plan={activePlan}
          isHistorical={isHistorical}
          className={variant === 'floating' ? 'max-h-44' : 'max-h-40'}
        />
      )}
    </div>
  );
}

/**
 * Automation-scoped Todo card (editor chat, agent `quill2`).
 */
export function RainTodoCard({
  automationId,
  agentId,
  threadId,
  variant,
  isSending,
  isRetired,
  className,
}: IRainTodoCardProps) {
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: activePlanResponse, isError } = useAutomationGetActivePlan(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    agentId,
    threadId,
    undefined,
    {
      query: {
        enabled: Boolean(agentId && threadId),
        refetchInterval: isSending ? POLL_INTERVAL_MS : false,
        retry: false,
        staleTime: 0,
      },
    },
  );

  return (
    <RainTodoCardView
      activePlan={activePlanResponse?.data}
      isError={isError}
      variant={variant}
      isRetired={isRetired}
      className={className}
    />
  );
}

/**
 * Run-scoped Todo card (run-page guidance chat, agent `astral2`).
 */
export function RainRunTodoCard({
  automationId,
  runId,
  agentId,
  threadId,
  variant,
  isSending,
  isRetired,
  className,
}: IRainRunTodoCardProps) {
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: activePlanResponse, isError } = useRunGetActivePlan(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    runId,
    agentId,
    threadId,
    undefined,
    {
      query: {
        enabled: Boolean(agentId && threadId),
        refetchInterval: isSending ? POLL_INTERVAL_MS : false,
        retry: false,
        staleTime: 0,
      },
    },
  );

  return (
    <RainTodoCardView
      activePlan={activePlanResponse?.data}
      isError={isError}
      variant={variant}
      isRetired={isRetired}
      className={className}
    />
  );
}

interface ITodoStepListProps {
  plan: V1Plan;
  isHistorical?: boolean;
  className?: string;
}

/** Scrolls the steps, fading out only the edges that hide more of them. */
function TodoStepList({ plan, isHistorical, className }: ITodoStepListProps) {
  const { edges, track } = useScrollEdges();

  return (
    <div
      ref={track}
      className={cn(
        'no-scrollbar overflow-y-auto px-5 pt-1 pb-4',
        // Mask alpha only: the color never renders, so a theme token adds nothing
        '[mask-image:linear-gradient(to_bottom,transparent_0,black_var(--fade-top),black_calc(100%_-_var(--fade-bottom)),transparent_100%)]',
        edges.top ? '[--fade-top:28px]' : '[--fade-top:0px]',
        edges.bottom ? '[--fade-bottom:28px]' : '[--fade-bottom:0px]',
        className,
      )}
      data-testid="rain-todo-step-list"
    >
      <RainPlanView plan={plan} isHistorical={isHistorical} />
    </div>
  );
}

/**
 * A finished plan parked in the transcript, collapsed. Rebuilt from `plan_*`
 * events: the server keeps only the active plan, so this is the only record.
 */
export function RainStreamPlanCard({
  plan,
  className,
}: IRainStreamPlanCardProps) {
  return (
    <RainTodoCardView
      activePlan={plan}
      isError={false}
      variant="stream"
      isHistorical
      className={className}
    />
  );
}

(RainStreamPlanCard as TRainFullWidthCard)[RAIN_FULL_WIDTH_CARD] = true;
